import type { ReactElement } from 'react';
import { EmptyState } from '../feedback/index.js';
import { AvailabilitySummary } from './AvailabilitySummary.js';
import { AvailabilityTimeline, formatRange, toSegments } from './AvailabilityTimeline.js';
import type { Availability } from '../../lib/api/queries/equipment.js';

/**
 * P-Q013, rendered under BR-9.8. Composes `AvailabilityTimeline` (the desktop
 * bar) and `AvailabilitySummary` (the text, which is the authoritative copy).
 *
 * Two rules shape this and neither is cosmetic:
 *
 *   · **A non-owner never learns WHY.** The API sends `reason: null` to anyone
 *     but the owner, so an unavailable window renders as a date range and
 *     nothing else. Showing "booked" or "maintenance" would disclose another
 *     customer's business.
 *   · **Looking is not holding.** `note_key` is on every response precisely so
 *     the client cannot quietly imply exclusivity. It is rendered every time,
 *     never behind a tooltip.
 *
 * The backend returns intervals, not a month grid. Drawing a calendar would
 * mean inventing day cells for a window the caller chose arbitrarily, so an
 * empty result is an empty state rather than a blank grid.
 */
export interface AvailabilityViewProps {
  availability: Availability;
  /** The window that was requested, for the timeline's scale. */
  from: string;
  to: string;
}

export function AvailabilityView({ availability, from, to }: AvailabilityViewProps): ReactElement {
  const segments = toSegments(availability);

  return (
    <section className="availability" aria-labelledby="availability-heading">
      <h2 id="availability-heading" className="section-title">
        Availability
      </h2>

      <p className="availability__note">
        This is what the owner’s calendar shows for {formatRange(from, to)}. Availability is
        informational until a hold is created — no dates are held for you until a booking is made.
      </p>

      {segments.length === 0 ? (
        <EmptyState
          title="No availability published for these dates"
          body="The owner has not published a calendar covering this window. Ask them directly when you request terms."
        />
      ) : (
        <>
          <AvailabilityTimeline segments={segments} from={from} to={to} />
          <AvailabilitySummary segments={segments} constraints={availability.constraints} />
        </>
      )}

      <p className="availability__asof">
        As at{' '}
        <time className="numeric" dateTime={availability.as_of}>
          {new Date(availability.as_of).toLocaleString()}
        </time>
      </p>
    </section>
  );
}
